import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, Card } from '@blueprintjs/core';
import './AboutPage.scss';

const AboutPage: React.FC = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-page__hero section--gradient">
        <div className="container">
          <div className="about-page__hero-content">
            <span className="about-page__eyebrow">Hi, I'm Rolita</span>
            <h1>I Help Families Protect What Matters Most</h1>
            <p>Life insurance that actually works while you're living</p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section">
        <div className="container">
          <div className="about-page__about">
            <div className="about-page__photo">
              <div className="about-page__photo-placeholder">
                <Icon icon="person" size={80} />
              </div>
            </div>
            <div className="about-page__bio">
              <h2>Let Me Tell You Why I Do This</h2>
              <p>
                I've seen what happens when families face medical emergencies without proper
                protection. The stress. The fear. The impossible choices between getting
                treatment and paying the mortgage.
              </p>
              <p>
                Here's the thing — most families <strong>think</strong> they're protected
                because they have life insurance. But traditional life insurance only pays
                when you die. What about when you're fighting cancer? Recovering from a heart
                attack? Living with a chronic condition?
              </p>
              <p>
                That's why I specialize in <strong>Living Benefits</strong>. It's life insurance
                that you can actually use while you're still alive. When you need money for
                treatment, to replace lost income, or just to focus on getting better without
                financial stress — you can access your death benefit early.
              </p>
              <p className="about-page__bio-highlight">
                <strong>And here's what surprises most people:</strong> it typically costs the
                same as traditional life insurance. You're not paying extra for this protection.
                You're just getting smarter coverage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters Section */}
      <section className="section section--light">
        <div className="container">
          <div className="about-page__why-matters">
            <h2>Why This Matters More Than Ever</h2>
            <div className="about-page__stats-context">
              <p>
                Americans are carrying more debt than ever before — <strong>over $14.9 trillion</strong> in
                consumer debt. Mortgages, car payments, credit cards, student loans. Most families are
                one serious illness away from financial disaster.
              </p>
              <p>
                Traditional life insurance was designed for a time when people either recovered quickly
                or passed away. Today, medicine keeps us alive — but the bills don't stop. And neither
                do your regular expenses.
              </p>
            </div>
            <div className="about-page__reality-check">
              <div className="about-page__reality-item">
                <Icon icon="warning-sign" size={24} />
                <p><strong>70% of families</strong> are underinsured and don't even know it</p>
              </div>
              <div className="about-page__reality-item">
                <Icon icon="warning-sign" size={24} />
                <p><strong>66% of bankruptcies</strong> are tied to medical issues</p>
              </div>
              <div className="about-page__reality-item">
                <Icon icon="warning-sign" size={24} />
                <p><strong>Most policies</strong> only pay after death — not when you need it most</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* My Approach Section */}
      <section className="section">
        <div className="container">
          <div className="about-page__approach">
            <h2>How I Work With Families</h2>
            <p className="about-page__approach-intro">
              I don't do high-pressure sales. I don't use confusing jargon. I just have honest
              conversations about what you need and help you find the right protection.
            </p>
            <div className="about-page__values">
              <Card className="about-page__value-card">
                <Icon icon="chat" size={32} />
                <h3>Real Conversations</h3>
                <p>
                  I take time to understand your situation — your debts, your family, your concerns.
                  No cookie-cutter solutions. Just honest advice based on what you actually need.
                </p>
              </Card>
              <Card className="about-page__value-card">
                <Icon icon="lightbulb" size={32} />
                <h3>Education First</h3>
                <p>
                  Most people don't know Living Benefits exist. I make sure you understand all your
                  options so you can make an informed decision. Knowledge is power.
                </p>
              </Card>
              <Card className="about-page__value-card">
                <Icon icon="hand" size={32} />
                <h3>No Pressure. Ever.</h3>
                <p>
                  This is your decision, your family, your future. My job is to give you the
                  information you need. The rest is up to you — on your timeline.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section section--light">
        <div className="container">
          <div className="text-center mb-5">
            <h2>What I Can Help You With</h2>
            <p className="about-page__section-subtitle">
              Whether you need new coverage or want to review what you have,
              I'm here to help you figure it out.
            </p>
          </div>
          <div className="about-page__services">
            <div className="about-page__service">
              <div className="about-page__service-icon">
                <Icon icon="shield" size={28} />
              </div>
              <div className="about-page__service-content">
                <h3>Living Benefits Life Insurance</h3>
                <p>
                  The coverage I'm most passionate about. Access your death benefit while
                  you're still alive if you face a critical illness, chronic condition, or
                  serious injury. This is protection that actually works when you need it.
                </p>
              </div>
            </div>
            <div className="about-page__service">
              <div className="about-page__service-icon">
                <Icon icon="home" size={28} />
              </div>
              <div className="about-page__service-content">
                <h3>Mortgage Protection</h3>
                <p>
                  Your home is probably your biggest debt and your family's biggest asset.
                  Make sure they can stay there no matter what happens to you.
                </p>
              </div>
            </div>
            <div className="about-page__service">
              <div className="about-page__service-icon">
                <Icon icon="search" size={28} />
              </div>
              <div className="about-page__service-content">
                <h3>Policy Reviews</h3>
                <p>
                  Already have life insurance? Let's look at it together. Many older policies
                  don't include Living Benefits — and you might be paying for less protection
                  than you think.
                </p>
              </div>
            </div>
            <div className="about-page__service">
              <div className="about-page__service-icon">
                <Icon icon="people" size={28} />
              </div>
              <div className="about-page__service-content">
                <h3>Family Planning</h3>
                <p>
                  Growing family? Changing circumstances? Let's make sure your coverage grows
                  with you. I'll help you figure out how much you actually need.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Touch Section */}
      <section className="section">
        <div className="container">
          <div className="about-page__personal">
            <h2>A Few Things About Me</h2>
            <div className="about-page__personal-content">
              <p>
                I got into this business because I believe everyone deserves to understand
                their options. Too many people are sold policies they don't understand,
                or worse — they're not protected at all because insurance seems too confusing.
              </p>
              <p>
                When I'm not helping families with their insurance needs, I'm probably spending
                time with my own family — which is exactly why I understand how important this is.
                We all want to protect the people we love.
              </p>
              <p>
                <strong>What makes me different?</strong> I actually care about getting you the
                right coverage, not the biggest commission. If Living Benefits aren't right for
                your situation, I'll tell you. If you're already well-protected, I'll tell you
                that too. My goal is to help, not to sell.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section--dark">
        <div className="container">
          <div className="about-page__cta">
            <h2>Let's Have a Conversation</h2>
            <p className="about-page__cta-personal">
              No pressure. No obligation. Just a real conversation about
              protecting the people you love.
            </p>
            <p>
              Whether you have questions, want to review your current coverage, or
              are ready to explore Living Benefits — I'm here to help. And the
              consultation is always free.
            </p>
            <div className="about-page__cta-buttons">
              <Link to="/contact">
                <Button
                  intent="success"
                  large
                  rightIcon={<Icon icon="arrow-right" />}
                >
                  Get in Touch
                </Button>
              </Link>
              <Link to="/living-benefits">
                <Button large outlined className="about-page__cta-secondary">
                  Learn About Living Benefits
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
